import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { api, setCsrfToken } from "./api.js";

export interface MeUser {
  id: string;
  email: string;
  displayName: string;
  emailVerifiedAt: string | null;
  createdAt: string;
}

export interface MeWorkspace {
  id: string;
  name: string;
  isPersonal: boolean;
}

export interface MeIgAccount {
  id: string;
  igUserId: string;
  igUsername: string;
  profilePicUrl: string | null;
  status: "active" | "reauth_required" | "suspended" | "disconnected";
}

export interface WorkspaceListItem {
  id: string;
  name: string;
  isPersonal: boolean;
  role: string;
  memberCount: number;
}

export type AuthStatus = "loading" | "anon" | "authed";

interface AuthState {
  status: AuthStatus;
  user: MeUser | null;
  workspace: MeWorkspace | null;
  workspaces: WorkspaceListItem[];
  activeIgAccount: MeIgAccount | null;
}

interface AuthContextValue extends AuthState {
  /** Re-fetches /me + /me/workspaces + /auth/csrf. Call after login / verify / switch. */
  refresh: () => Promise<void>;
  /** Switches active workspace + refreshes derived state. */
  switchWorkspace: (workspaceId: string) => Promise<void>;
  /** Switches active IG account within the current workspace. */
  switchIgAccount: (igAccountId: string) => Promise<void>;
  /** Logs out + clears state. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface MeResponse {
  user: MeUser;
  activeWorkspace: MeWorkspace;
  role: string;
  activeIgAccount: MeIgAccount | null;
}

interface WorkspacesResponse {
  workspaces: WorkspaceListItem[];
}

interface CsrfResponse {
  csrfToken: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    status: "loading",
    user: null,
    workspace: null,
    workspaces: [],
    activeIgAccount: null,
  });

  const refresh = useCallback(async () => {
    try {
      const [meRes, wsRes, csrfRes] = await Promise.all([
        api.get<MeResponse>("/me"),
        api.get<WorkspacesResponse>("/me/workspaces"),
        api.get<CsrfResponse>("/auth/csrf"),
      ]);
      setCsrfToken(csrfRes.data.csrfToken);
      setState({
        status: "authed",
        user: meRes.data.user,
        workspace: meRes.data.activeWorkspace,
        workspaces: wsRes.data.workspaces,
        activeIgAccount: meRes.data.activeIgAccount,
      });
    } catch {
      setCsrfToken(null);
      setState({
        status: "anon",
        user: null,
        workspace: null,
        workspaces: [],
        activeIgAccount: null,
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      await api.post("/me/active-workspace", { workspaceId });
      await refresh();
    },
    [refresh],
  );

  const switchIgAccount = useCallback(
    async (igAccountId: string) => {
      const res = await api.post<{ activeIgAccount: MeIgAccount }>(
        "/me/active-ig-account",
        { igAccountId },
      );
      setState((s) => ({ ...s, activeIgAccount: res.data.activeIgAccount }));
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Already logged out is fine.
    }
    setCsrfToken(null);
    setState({
      status: "anon",
      user: null,
      workspace: null,
      workspaces: [],
      activeIgAccount: null,
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, refresh, switchWorkspace, switchIgAccount, logout }),
    [state, refresh, switchWorkspace, switchIgAccount, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used inside <AuthProvider>");
  return ctx;
}
